<script lang="ts" setup>
import { ref, computed } from "vue";
import { useFetch } from "#app";
import type { IMedia } from "~/types/IMedia";

definePageMeta({
  layout: "default",
});

const { t } = useI18n();
const runtimeConfig = useRuntimeConfig();
const folderLink = runtimeConfig.public.folderLink || '';
const searchQuery = ref('');

// Récupération de tous les médias
const { data: media, error, pending } = await useFetch('/api/media/getAllMedia', {
  method: 'GET',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Fonction pour récupérer le logo d'un média depuis les fichiers uploadés
async function getMediaLogo(mediaId: string): Promise<string> {
  try {
    const { data: filesData } = await useFetch(`/api/upload/${mediaId}`, { method: 'GET' });
    const files = (filesData.value as any[]) || [];
    
    // Chercher le fichier qui correspond au logo
    const logoFile = files.find((f: any) => f.path && f.path.includes('logo'));
    return logoFile ? folderLink + logoFile.path : '';
  } catch (error) {
    console.error('Error fetching media logo:', error);
    return '';
  }
}

// Fonction pour obtenir l'URL complète du logo
function getLogoUrl(mediaItem: IMedia): string {
  if (mediaItem.logo) {
    return folderLink + mediaItem.logo;
  }
  return '';
}

// Fonction pour extraire l'ID YouTube d'une URL
function extractYouTubeId(url: string): string | null {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

// Fonction pour obtenir la miniature YouTube
function getYouTubeThumbnail(url: string): string {
  const videoId = extractYouTubeId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '';
}

// Fonction pour filtrer les médias basée sur la recherche
const filteredMedia = computed(() => {
  if (!media.value?.media) return [];
  
  const mediaList = media.value.media as IMedia[];
  
  if (!searchQuery.value.trim()) {
    return mediaList;
  }
  
  return mediaList.filter((media: IMedia) =>
    media.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    (media.description && media.description.toLowerCase().includes(searchQuery.value.toLowerCase()))
  );
});

// Fonction pour gérer le clic sur un média
function handleMediaClick(media: IMedia) {
  if (media.link) {
    window.open(media.link, '_blank', 'noopener,noreferrer');
  } else if (media.youtube) {
    window.open(media.youtube, '_blank', 'noopener,noreferrer');
  }
}
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <div class="bg-white shadow">
      <div class="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
        <div class="text-center mt-12">
          <h1 class="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
            {{ t('media.title') }}
          </h1>
          <p class="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            {{ t('media.description') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Search Section -->
    <div class="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div class="max-w-md mx-auto">
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <UIcon name="i-heroicons-magnifying-glass" class="h-5 w-5 text-gray-400" />
          </div>
          <UInput
            v-model="searchQuery"
            :placeholder="t('media.search-placeholder')"
            size="lg"
            class="w-full pl-10"
          />
        </div>
      </div>
    </div>

    <div v-if="pending" class="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div class="text-center">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-greenCharlys-30"></div>
        <p class="mt-2 text-gray-600">{{ t('media.loading') }}</p>
      </div>
    </div>

    <div v-else-if="error" class="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div class="text-center">
        <div class="text-red-500 text-lg">{{ t('media.error') }}</div>
      </div>
    </div>

    <div v-else class="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div v-if="filteredMedia.length === 0" class="text-center py-12">
        <div class="text-gray-500 text-lg">
          {{ searchQuery ? t('media.no-results') : t('media.no-media') }}
        </div>
      </div>

      <!-- Media grid -->
      <div v-else class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <div
          v-for="mediaItem in filteredMedia"
          :key="mediaItem._id"
          class="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer group"
          @click="handleMediaClick(mediaItem)"
        >
          <div class="p-6">
            <div class="flex justify-center mb-4">
              <img 
                v-if="mediaItem.logo" 
                :src="getLogoUrl(mediaItem)" 
                :alt="mediaItem.name" 
                class="h-24 w-24 object-contain" 
                @error="($event.target as HTMLImageElement).style.display='none'"
              />
              
              <div v-else-if="mediaItem.youtube" class="relative h-24 w-24">
                <img
                  :src="getYouTubeThumbnail(mediaItem.youtube)"
                  :alt="mediaItem.name"
                  class="h-full w-full object-cover rounded-lg"
                  @error="($event.target as HTMLImageElement).style.display='none'"
                />
                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="bg-red-600 rounded-full p-2">
                    <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              </div>
              
              <div v-else class="h-24 w-24 bg-gray-100 flex items-center justify-center rounded-lg">
                <span class="text-gray-400 text-2xl font-bold">
                  {{ mediaItem.name.charAt(0).toUpperCase() }}
                </span>
              </div>
            </div>

            <h3 class="text-lg font-semibold text-gray-900 text-center mb-2 group-hover:text-greenCharlys-30 transition-colors">
              {{ mediaItem.name }}
            </h3>

            <p v-if="mediaItem.description" class="text-gray-600 text-sm text-center line-clamp-3">
              {{ mediaItem.description }}
            </p>

            <!-- Indicateur de type de média -->
            <div class="mt-2 flex justify-center">
              <span v-if="mediaItem.youtube" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                YouTube
              </span>
              <span v-else-if="mediaItem.link" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
                Lien
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="h-16"></div>
  </div>
</template>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  line-clamp: 3;
  overflow: hidden;
}
</style>
